import type { ReactNode, JSX } from 'react'
import { useLocation } from 'react-router-dom'

import { CoachMark } from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { schedule } from '~publish/legacy/routes'

import { usePublishAccessScope } from './usePublishAccessScope'
import { ViewChannelPermissionsActions } from './ViewChannelPermissionsActions'

import styles from './ViewOnlyCoachMarks.module.css'

/**
 * Server-persisted, per-account flag (via `account.dismissedBanners`) for the
 * one-time coach mark shown the first time an actor with mixed Publish access
 * (some channels view-only, some postable) lands on the All Channels view.
 * Dismissed forever once acknowledged.
 */
export const PUBLISH_MIXED_ACCESS_COACH_MARK_BANNER =
  'publish-mixed-access-coach-mark'

type MixedPublishAccessCoachMarkProps = {
  /** The Publish item in the site navigation the coach mark anchors to. */
  children: ReactNode
}

/**
 * First-visit education for actors whose Publish access differs across
 * channels: a pulsating beacon over the Publish nav item
 * with a popover explaining that some channels are view-only. Shown only on
 * the All Channels views (`/schedule` list and calendar) — the place where
 * the differing access levels sit side by side. The primary action opens the
 * member's channel access (see {@link ViewChannelPermissionsActions}); either
 * action persists the dismissal per account, after which the nav item renders
 * untouched.
 *
 * Mutually exclusive with {@link ViewOnlyPublishCoachMark} via
 * {@link usePublishAccessScope}: an actor who is view-only everywhere never
 * reaches `mixed`, so they get the badge-anchored coach mark instead.
 */
export const MixedPublishAccessCoachMark = ({
  children,
}: MixedPublishAccessCoachMarkProps): JSX.Element => {
  const scope = usePublishAccessScope()
  const { pathname } = useLocation()
  const { isActive, dismiss } = useDismissableBanner(
    PUBLISH_MIXED_ACCESS_COACH_MARK_BANNER,
    { trackDismiss: true },
  )

  const isAllChannelsView =
    pathname === schedule.route || pathname.startsWith(`${schedule.route}/`)

  if (scope !== 'mixed' || !isAllChannelsView || !isActive) {
    return <>{children}</>
  }

  return (
    <CoachMark open onDismiss={dismiss} dismissTimeout={null}>
      {/* Span the full nav-item width so the beacon centres over the item.
          Set via `style` (not a className) so Popcorn's own `.overlay` class —
          which positions the spotlight — is preserved. */}
      <CoachMark.Overlay style={{ width: '100%' }}>
        {children}
        <CoachMark.Spotlight size="small" />
      </CoachMark.Overlay>
      <CoachMark.Content
        side="right"
        align="center"
        showCloseIcon={false}
        className={styles.content}
      >
        <CoachMark.Title>
          You have different Publish access across channels
        </CoachMark.Title>
        <CoachMark.Description>
          Some channels are view-only. You can see posts but can&apos;t create,
          edit, or move them. Ask an admin to update your permissions.
        </CoachMark.Description>
        <CoachMark.Footer>
          <ViewChannelPermissionsActions onDismiss={dismiss} />
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
